import { Column, Header } from "@tanstack/react-table";
import { useEffect, useRef } from "react";

interface UseColumnOrderProps {
  tableName: string;
  getAllLeafColumns: () => Column<any, unknown>[];
  setColumnOrder: (order: string[] | ((old: string[]) => string[])) => void;
}

interface UseColumnOrderReturn {
  resetColumnOrder: () => void;
  updateColumnOrderOnVisibilityChange: (
    columnName: string,
    visibility: boolean
  ) => void;
  onMoveHeaderToRight: (header: Header<any, unknown>) => void;
  onMoveHeaderToLeft: (header: Header<any, unknown>) => void;
}

const arrayMove = (arr: string[], id: string, direction: "plus" | "minus"): string[] => {
  const index = arr.indexOf(id);
  if (index === -1) return arr;

  const newIndex = direction === "plus" ? index + 1 : index - 1;
  if (newIndex < 0 || newIndex >= arr.length) return arr;

  const result = [...arr];
  [result[index], result[newIndex]] = [result[newIndex], result[index]];
  return result;
};

export const useColumnOrder = ({
  tableName,
  getAllLeafColumns,
  setColumnOrder,
}: UseColumnOrderProps): UseColumnOrderReturn => {
  const defaultColumnOrder = useRef<string[]>([]);

  // Helper: Build default column order from visible columns
  const buildDefaultColumnOrder = (): string[] => {
    return getAllLeafColumns()
      .filter((col) => col.getIsVisible())
      .map((col) => col.id);
  };

  // LocalStorage helpers
  const getStoredOrder = (): string[] | null => {
    try {
      const stored = localStorage.getItem(`${tableName}Order`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const setStoredOrder = (order: string[]): void => {
    localStorage.setItem(`${tableName}Order`, JSON.stringify(order));
  };

  const removeStoredOrder = (): void => {
    localStorage.removeItem(`${tableName}Order`);
  };

  // Initialize column order on mount
  useEffect(() => {
    const defaultOrder = buildDefaultColumnOrder();
    defaultColumnOrder.current = defaultOrder;

    const storedOrder = getStoredOrder();
    if (storedOrder) {
      // Validate stored order contains valid column IDs
      const allColumnIds = getAllLeafColumns().map((col) => col.id);
      const validOrder = storedOrder.filter((id) => allColumnIds.includes(id));

      if (validOrder.length > 0) {
        setColumnOrder(validOrder);
      } else {
        setColumnOrder(defaultOrder);
      }
    } else {
      setColumnOrder(defaultOrder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset column order to default
  const resetColumnOrder = (): void => {
    const defaultOrder = buildDefaultColumnOrder();
    defaultColumnOrder.current = defaultOrder;
    setColumnOrder(defaultOrder);
    removeStoredOrder();
  };

  // Update column order when visibility changes
  const updateColumnOrderOnVisibilityChange = (
    columnName: string,
    visibility: boolean
  ): void => {
    setColumnOrder((old) => {
      let result: string[];
      if (visibility) {
        // Add column at the end if not present
        result = old.includes(columnName) ? old : [...old, columnName];
      } else {
        // Remove column from order
        result = old.filter((id) => id !== columnName);
      }
      setStoredOrder(result);
      return result;
    });
  };

  // Move header to the left (swap with previous)
  const onMoveHeaderToRight = (header: Header<any, unknown>): void => {
    setColumnOrder((old) => {
      const result = arrayMove(old, header.id, "minus");
      setStoredOrder(result);
      return result;
    });
  };

  // Move header to the right (swap with next)
  const onMoveHeaderToLeft = (header: Header<any, unknown>): void => {
    setColumnOrder((old) => {
      const result = arrayMove(old, header.id, "plus");
      setStoredOrder(result);
      return result;
    });
  };

  return {
    resetColumnOrder,
    updateColumnOrderOnVisibilityChange,
    onMoveHeaderToRight,
    onMoveHeaderToLeft,
  };
};
